import React, { ReactElement, useMemo } from "react";

import { ExclamationCircleIcon } from "@heroicons/react/20/solid";

import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  Path,
  useFormContext,
} from "react-hook-form";
import { ModalLabel } from "./ModalLabel";
import { cn } from "helpers/classNames";

type Props<T extends FieldValues> =
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    name: Path<T>;
    label?: string;
    error?: string;
    labelClassName?: string;
    required?: boolean;
    render?: (field: ControllerRenderProps<T>) => ReactElement;
    disabled?: boolean;
    customSize?: "third" | "half" | "full";
  };

export function TextAreaFormProvider<T extends FieldValues>(props: Props<T>) {
  const {
    name,
    label,
    error,
    required,
    className,
    labelClassName,
    render,
    disabled = false,
    customSize,
    ...rest
  } = props;

  const { control, register, formState } = useFormContext<T>();

  const errorMessage = useMemo(() => {
    if (error) {
      return error;
    }

    const fieldError = formState.errors[name];
    return fieldError ? (fieldError.message as string) : undefined;
  }, [error, formState.errors, name]);

  const sizeClass = useMemo(() => {
    switch (customSize) {
      case "third":
        return "w-1/2 sm:w-1/3";
      case "half":
        return "sm:w-1/2";
      case "full":
        return "";
    }
  }, [customSize]);

  // const sizeClass =
  //   size === "third" ? "w-1/2 sm:w-1/3" : size === "half" ? "sm:w-1/2" : "";
  return (
    <div className={className}>
      {label && (
        <ModalLabel className={labelClassName}>
          {label}
          {required && "*"}
        </ModalLabel>
      )}
      <div className={cn("relative mt-1 rounded-md shadow-sm", sizeClass)}>
        {render ? (
          <Controller
            name={name}
            control={control}
            render={({ field }) => render(field)}
          />
        ) : (
          <textarea
            {...register(name as Path<T>)}
            className={cn(
              "block w-full rounded-md shadow-sm sm:text-sm",
              errorMessage
                ? "border-red-300 pr-10 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500"
                : "border-gray-300 focus:border-cyan-500 focus:ring-cyan-500",
            )}
            autoComplete="off"
            disabled={disabled}
            {...rest}
          />
        )}
        {!!errorMessage && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <ExclamationCircleIcon
              className="h-5 w-5 text-red-500"
              aria-hidden="true"
            />
          </div>
        )}
      </div>
      {!!errorMessage && (
        <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
      )}
    </div>
  );
}
